import { useState, useRef, useMemo } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  ScrollArea,
  cn,
} from '@design-system';

import { Check, ChevronsUpDown } from 'lucide-react';

interface QuestionComboboxOption {
  value: string;
  label: string;
  selectedDisplay?: string | React.ReactNode;
  optionDisplay?: React.ReactNode;
}

interface QuestionComboboxProps {
  value: string;
  options: QuestionComboboxOption[];
  onChange: (value: string | null) => void;
  placeholder?: string;
  className?: string;
}

export function QuestionCombobox({
  value,
  options,
  onChange,
  placeholder = 'Select a question...',
  className,
}: QuestionComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find(option => option.value === value);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lower = search.toLowerCase();
    return options.filter(
      option =>
        option.value.toLowerCase().includes(lower) ||
        (typeof option.label === 'string' && option.label.toLowerCase().includes(lower)),
    );
  }, [options, search]);

  return (
    <>
      <Button
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className={cn('w-full justify-between font-normal', className)}
        onClick={() => setOpen(true)}
      >
        <span className="truncate">
          {selectedOption ? (selectedOption.selectedDisplay ?? selectedOption.label) : placeholder}
        </span>
        <ChevronsUpDown className="shrink-0 opacity-50" />
      </Button>

      <Dialog
        open={open}
        onOpenChange={isOpen => {
          setOpen(isOpen);
          if (!isOpen) setSearch('');
        }}
      >
        <DialogContent className="gap-0 p-0">
          <DialogHeader className="border-b p-4">
            <DialogTitle>Select a Question</DialogTitle>
          </DialogHeader>
          <div className="border-b">
            <Input
              ref={inputRef}
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search questions..."
              autoFocus
              className="border-0 px-4 focus-visible:border-transparent focus-visible:ring-0"
            />
          </div>
          <ScrollArea className="max-h-[60vh]">
            <div className="p-2">
              {filteredOptions.length === 0 ? (
                <p className="text-muted-foreground p-4 text-center text-sm">No questions found.</p>
              ) : (
                filteredOptions.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    className={cn(
                      'hover:bg-accent my-0.5 flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm',
                      value === option.value && 'bg-accent',
                    )}
                    onClick={() => {
                      onChange(option.value === value ? null : option.value);
                      setOpen(false);
                      setSearch('');
                    }}
                  >
                    <div className="min-w-0 flex-1">{option.optionDisplay ?? option.label}</div>
                    <Check
                      className={cn(
                        'size-4 shrink-0',
                        value === option.value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
